import { formatDate, NgClass } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import {
	AbstractControl,
	FormArray,
	FormBuilder,
	FormControl,
	FormGroup,
	ValidationErrors,
	Validators,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import {
	AddOnBilling,
	BillingContract,
	billingEndDateOptions,
	billingLabels,
	billingTriggerOptions,
	currencyTypeOptions,
	emptyContractSettingsFields,
	mrcOrNrcOptions,
	unitsOfMeasureOptions,
} from 'src/app/models/billingContractAndOptions';
import { ApiService } from 'src/app/services/api.services';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import AddOnBillingSettings = Components.Schemas.AddOnBillingSettings;
import { EndBillingDialogComponent } from '../end-billing-dialog/end-billing-dialog.component';
import { SaveChangesDialogComponent } from '../save-changes-dialog/save-changes-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDivider } from '@angular/material/list';
import { BillingStatusComponent } from '../../../components/billing-status/billing-status.component';
import {
	MatFormField,
	MatSuffix,
	MatLabel,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import {
	MatDatepickerInput,
	MatDatepickerToggle,
	MatDatepickerToggleIcon,
	MatDatepicker,
} from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatButton } from '@angular/material/button';
import { BillingStartDateFilterPipe } from '../../../components/pipes';

@Component({
	selector: 'app-billing-edit-contract',
	templateUrl: './billing-edit-contract.component.html',
	styleUrls: ['./billing-edit-contract.component.scss'],
	imports: [
		NgClass,
		MatDivider,
		BillingStatusComponent,
		MatFormField,
		MatInput,
		FormsModule,
		MatDatepickerInput,
		ReactiveFormsModule,
		MatDatepickerToggle,
		MatSuffix,
		MatIcon,
		MatDatepickerToggleIcon,
		MatDatepicker,
		MatSelect,
		MatOption,
		MatLabel,
		MatButton,
		BillingStartDateFilterPipe,
	],
})
export class BillingEditContractComponent implements OnInit {
	@Input() billingContract!: BillingContract;
	@Input() userAction: 'viewing' | 'editing' | 'reviewing' = 'reviewing'; // this determines if the user is viewing, editing, or reviewing the form
	billingStartDate!: FormControl;
	billingEndDate!: FormControl;
	addOnBillingFormGroup!: FormGroup;
	minBillingStartDate: Date = new Date(new Date().setHours(0, 0, 0, 0));

	filterLabels = billingLabels;
	billingTriggerOptions = billingTriggerOptions;
	billingEndDateOptions = billingEndDateOptions;
	unitsOfMeasureOptions = unitsOfMeasureOptions;
	mrcOrNrcOptions = mrcOrNrcOptions;
	currencyTypeOptions = currencyTypeOptions;
	companyEditsSavedText = $localize`:@@billingContract-companyBillingEditsSaved:Company billing edits have been saved.`;

	constructor(
		private fb: FormBuilder,
		private readonly apiService: ApiService,
		private readonly dialog: MatDialog,
		private readonly snackbar: MatSnackBar
	) {
		this.addOnBillingFormGroup = this.fb.group({
			addOnBillingFormArray: this.fb.array([]),
		});
		this.addAddOnBillingOption();
	}

	ngOnInit(): void {
		this.billingStartDate = new FormControl(
			this.billingContract.billingStartDate,
			[Validators.required, this.validDateValidator]
		);
		this.billingEndDate = new FormControl(
			this.billingContract.isMonthlyAutoRenewed,
			Validators.required
		);
		this.addOnBillingFormGroup.valueChanges.subscribe((changes) => {
			this.isFormValid();
		});

		// Safeguard to make sure the user does not go into editing mode when they're not allowed to
		if (
			this.userAction === 'editing' &&
			this.billingContract.status === 'manuallyEnded'
		) {
			this.userAction = 'viewing';
		}
	}

	findObjectByKey(array: any, key: any) {
		const findObj = array.find((item: any) => item.key === key);
		return findObj;
	}

	isFormValid() {
		// Check if everything else is valid before add-on billing settings
		if (!this.billingStartDate.valid) {
			return false;
		}

		// Check if add-on billing controls valid
		if (
			(this.addOnBillingFormGroup.get('addOnBillingFormArray') as FormArray)
				?.controls
		) {
			for (const control of (
				this.addOnBillingFormGroup.get('addOnBillingFormArray') as FormArray
			)?.controls) {
				if (!this.allControlsAreBlankOrValidInAddOnBilling(control)) {
					return false;
				}
			}
		}

		return true;
	}

	allControlsAreBlankOrValidInAddOnBilling(group: AbstractControl) {
		// If all of the form controls in the form group are blank or are valid, then the form group is valid
		if (group instanceof FormGroup) {
			let allAreBlank = true;
			let allAreValid = true;
			for (const key of Object.keys(group.controls)) {
				const control = group.get(key);
				if (control) {
					if (control.invalid) {
						allAreValid = false;
					}
					if (
						(key !== 'recurringChargeTypeId' && control.value.trim().length) ||
						(key === 'recurringChargeTypeId' && control.value !== null)
					) {
						allAreBlank = false;
					}
				}
			}

			if (allAreBlank || allAreValid) {
				return true;
			}
			return false;
		}
		return true;
	}

	addAddOnBillingOption(event?: any) {
		if (event) {
			event.preventDefault();
		}
		const newAddOnBillingFormControl = this.fb.group({
			description: [
				'',
				[
					Validators.required,
					this.noWhitespaceValidator,
					Validators.maxLength(250),
				],
			],
			quantity: ['', [Validators.required, Validators.pattern(/^\s*\d*\s*$/)]],
			unitPrice: [
				'',
				[
					Validators.required,
					Validators.pattern(
						/^\s*[+-]?(\d+|\d*\.\d+|\d+\.\d*)([Ee][+-]?\d+)?\s*$/
					),
				],
			],
			recurringChargeTypeId: [null, [Validators.required]],
		});

		(this.addOnBillingFormGroup.get('addOnBillingFormArray') as FormArray).push(
			newAddOnBillingFormControl
		);
		this.addOnBillingFormGroup
			.get('addOnBillingFormArray')
			?.updateValueAndValidity();
	}

	noWhitespaceValidator(control: FormControl) {
		return (control.value || '').trim().length ? null : { whitespace: true };
	}

	validDateValidator(control: FormControl): ValidationErrors | null {
		// Check if valid date
		if (!control.value || typeof control.value.getMonth !== 'function') {
			return { InvalidDate: true };
		}
		return null;
	}

	billingDateOrFutureDateFilter = (d: Date | null): boolean => {
		const date = d || new Date();
		const today = new Date();

		// Allow the date to be in the future or the date of the original contract
		if (
			date.toISOString().split('T')[0] ===
			this.billingContract.billingStartDate.toISOString().split('T')[0]
		) {
			return true;
		}

		// Disable past dates
		return date >= today;
	};

	getControls() {
		if (this.addOnBillingFormGroup.get('addOnBillingFormArray') as FormArray) {
			return (
				this.addOnBillingFormGroup.get('addOnBillingFormArray') as FormArray
			).controls;
		}
		return [] as AbstractControl<any, any>[];
	}

	isValidControlForOnBillingFormGroup(
		group: AbstractControl<any, any>,
		controlName: string
	) {
		if (group instanceof FormGroup) {
			if ((group as FormGroup).controls[controlName].valid) {
				return true;
			}

			// If other controls aren't blank and this one is make it invalid
			for (const key of Object.keys(group.controls)) {
				if (key !== controlName) {
					const control = group.get(key);
					if (control) {
						if (
							(key !== 'recurringChargeTypeId' &&
								control.value.trim().length) ||
							(key === 'recurringChargeTypeId' && control.value !== null)
						) {
							return false;
						}
					}
				}
			}
		}
		return true;
	}

	isNumberKey(event: any) {
		const charCode = event.which ? event.which : event.keyCode;
		if (charCode > 31 && (charCode < 48 || charCode > 57)) {
			return false;
		}
		return true;
	}

	isNumberOrPeriodKey(event: any) {
		const charCode = event.which ? event.which : event.keyCode;
		if (charCode > 31 && charCode != 46 && (charCode < 48 || charCode > 57)) {
			return false;
		}
		return true;
	}

	removeAddOnBillingOption(i: number) {
		(
			this.addOnBillingFormGroup.get('addOnBillingFormArray') as FormArray
		).removeAt(i);
	}

	resetAddOnBillingOptions() {
		for (
			let i = 0;
			i <
			(this.addOnBillingFormGroup.get('addOnBillingFormArray') as FormArray)
				.length;
			i++
		) {
			this.removeAddOnBillingOption(i);
		}
		this.addAddOnBillingOption();
	}

	onEditClick(): void {
		this.userAction = 'editing';
	}

	useViewingMode(): void {
		this.userAction = 'viewing';
	}

	async onEndBillingClick() {
		const dialogRef: MatDialogRef<EndBillingDialogComponent> = this.dialog.open(
			EndBillingDialogComponent,
			{
				maxWidth: '510px',
				panelClass: 'pop-up-dialog-v2',
				data: {
					id: this.billingContract.id!,
					companyName: this.billingContract.company.name,
				},
			}
		);

		dialogRef.afterClosed().subscribe((result: any) => {
			if (!!result) {
				this.billingContract.status = 'manuallyEnded';
				this.resetAddOnBillingOptions();
				this.useViewingMode();
			}
		});
	}

	async onSaveChangesClick() {
		if (!this.isFormValid()) {
			return;
		}

		const addOnBilling: AddOnBilling[] = [];
		if (
			(this.addOnBillingFormGroup?.controls.addOnBillingFormArray as FormArray)
				?.controls
		) {
			for (const group of (
				this.addOnBillingFormGroup?.controls.addOnBillingFormArray as FormArray
			)?.controls) {
				if (group instanceof FormGroup) {
					if (group.valid) {
						addOnBilling.push({
							description: group.controls.description.value.trim(),
							quantity: group.controls.quantity.value,
							unitPrice: Number(group.controls.unitPrice.value),
							recurringChargeTypeId:
								group.controls.recurringChargeTypeId.value.toString(),
						});
					}
				}
			}
		}

		const dialogRef: MatDialogRef<SaveChangesDialogComponent> =
			this.dialog.open(SaveChangesDialogComponent, {
				maxWidth: '510px',
				panelClass: 'pop-up-dialog-v2',
				data: {
					id: this.billingContract.id!,
					reqBody: {
						addOnBilling: addOnBilling as unknown as AddOnBillingSettings,
						billingStartDate: formatDate(
							this.billingStartDate.value,
							'yyyy-MM-dd',
							'en-US'
						),
						isMonthlyAutoRenewed: this.billingEndDate.value,
					},
				},
			});

		dialogRef.afterClosed().subscribe((result: any) => {
			if (!!result) {
				this.billingContract.isMonthlyAutoRenewed = this.billingEndDate.value;
				this.billingContract.billingStartDate = this.billingStartDate.value;
				this.billingContract.addOnBilling = [
					...this.billingContract.addOnBilling,
					...addOnBilling,
				];
				this.resetAddOnBillingOptions();
				this.useViewingMode();
				this.snackbar.open(this.companyEditsSavedText, undefined, {
					duration: 2000,
				});
			}
		});
	}
}
